// Lógica PURA de la bienvenida guiada (#1019, CC-CONSOLE#1512): a qué ruta se
// manda al usuario, en qué paso se retoma, qué objetivos tienen sentido en este
// tenant, cómo se arma la lista de zonas horarias y qué correos se pueden
// invitar. Sin React ni red, para poder probarla sola.

export const WELCOME_PATH = "/bienvenida";

/**
 * A dónde se entra al terminar u omitir la bienvenida (y a dónde se va si el
 * asistente no aplica): el MAPA. Una autoalta nueva llega con el Modo Sandbox
 * encendido, así que ahí ya hay unidades de ejemplo que ver.
 */
export const AFTER_ONBOARDING_PATH = "/map";

// ---------------------------------------------------------------------------
// Variantes y pasos
// ---------------------------------------------------------------------------

/**
 * `owner`: quien creó la empresa de autoalta. `invitee`: quien entró aceptando
 * una invitación; sólo completa SU perfil y elige SUS objetivos.
 */
export type OnboardingVariant = "owner" | "invitee";

/**
 * Pasos del dueño, en el mismo orden que el backend (`onboardingState.STEPS`).
 * `device` (CC-CONSOLE#1523) manda a /dispositivos/vincular y se puede saltar.
 */
export const STEPS = ["company", "goals", "device", "team", "done"] as const;

/** A dónde manda el paso «Vincula tu primer equipo»; `back` regresa a la bienvenida al terminar. */
export const LINK_DEVICE_FROM_WELCOME_PATH = "/dispositivos/vincular?back=/bienvenida";
/** Pasos del invitado (`onboardingState.INVITEE_STEPS`). */
export const INVITEE_STEPS = ["profile", "goals", "done"] as const;

export type OnboardingStep = (typeof STEPS)[number] | (typeof INVITEE_STEPS)[number];

export function stepsFor(variant: OnboardingVariant | null | undefined): readonly OnboardingStep[] {
  return variant === "invitee" ? INVITEE_STEPS : STEPS;
}

export const COMPANY_SIZES = ["1-5", "6-20", "21-50", "51-200", "200+"] as const;

export interface OnboardingProfile {
  industry: string | null;
  size: string | null;
  timezone: string | null;
}

/** Perfil de la persona invitada. `phone` es un teléfono de CONTACTO, no el de inicio de sesión. */
export interface OnboardingUserProfile {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  position: string | null;
}

/** Respuesta de `GET /onboarding/status`. Sólo `applicable` viene siempre. */
export interface OnboardingStatus {
  applicable: boolean;
  variant?: OnboardingVariant;
  steps?: string[];
  companyId?: string;
  companyName?: string | null;
  completed?: boolean;
  step?: string;
  goals?: string[];
  profile?: Partial<OnboardingProfile>;
  userProfile?: Partial<OnboardingUserProfile>;
  completedAt?: string | null;
  skippedAt?: string | null;
  /**
   * Fue al mapa con «Ver mi mapa con datos de ejemplo» sin cerrar el asistente
   * (CC-CONSOLE#1550): ya no se le manda a la bienvenida al entrar, pero puede
   * retomarla (`/bienvenida?retomar=1`, aviso del Sandbox).
   */
  deferredAt?: string | null;
  availableGoals?: string[];
  companySizes?: string[];
}

/** ¿Hay que mostrar el asistente? Sin respuesta (API caída) → no. */
export function isOnboardingPending(status: OnboardingStatus | null | undefined): boolean {
  return !!status && status.applicable === true && status.completed !== true;
}

/** Variante que dice el backend; una respuesta vieja sin el campo es la del dueño. */
export function variantOf(status: OnboardingStatus | null | undefined): OnboardingVariant {
  return status?.variant === "invitee" ? "invitee" : "owner";
}

/**
 * Ruta a la que entra el usuario al salir del pago, de «Elige tu licencia» o
 * del registro por invitación: la bienvenida si le aplica y no la ha
 * terminado; si no, a donde ya iba.
 */
export function entryPathFor(
  status: OnboardingStatus | null | undefined,
  fallback: string,
): string {
  return isOnboardingPending(status) && !status?.deferredAt ? WELCOME_PATH : fallback;
}

/** Marca (sessionStorage) para que el mapa reporte `first_map_view` tras la ruta corta. */
export const FIRST_MAP_VIEW_KEY = "wayak:first-map-view";

/** Ruta para retomar la configuración que quedó pendiente (CC-CONSOLE#1550). */
export const RESUME_WELCOME_PATH = `${WELCOME_PATH}?retomar=1`;

/** ¿Hay una configuración pendiente que se pueda retomar? (fue al mapa sin terminarla) */
export function canResumeOnboarding(status: OnboardingStatus | null | undefined): boolean {
  return isOnboardingPending(status) && Boolean(status?.deferredAt);
}

/**
 * ¿Se muestra la entrada rápida de la bienvenida («Ver mi mapa con datos de
 * ejemplo» / «Configurar mi cuenta»)? Al dueño que llega por primera vez: en el
 * primer paso, sin haber ido aún al mapa y sin pedir retomar.
 */
export function showsQuickStart(status: OnboardingStatus | null | undefined, resume: boolean): boolean {
  if (resume || !isOnboardingPending(status) || status?.deferredAt) return false;
  if (status?.variant === "invitee") return false;
  return resumeStepIndex(status?.step) === 0;
}

/** Índice del paso en el que se retoma; desconocido o ausente → el primero. */
export function resumeStepIndex(
  step: string | null | undefined,
  steps: readonly OnboardingStep[] = STEPS,
): number {
  const index = steps.indexOf(step as OnboardingStep);
  return index < 0 ? 0 : index;
}

export function nextStep(step: OnboardingStep, steps: readonly OnboardingStep[] = STEPS): OnboardingStep {
  return steps[Math.min(resumeStepIndex(step, steps) + 1, steps.length - 1)];
}

export function prevStep(step: OnboardingStep, steps: readonly OnboardingStep[] = STEPS): OnboardingStep {
  return steps[Math.max(resumeStepIndex(step, steps) - 1, 0)];
}

// ---------------------------------------------------------------------------
// Objetivos
// ---------------------------------------------------------------------------

export interface GoalTarget {
  /** Categoría del catálogo de apps (`config/apps/<tenant>-config.json`). */
  category: string;
  /** App de esa categoría que resuelve el objetivo. */
  app: string;
  /** Icono Remix de la tarjeta. */
  icon: string;
}

/**
 * OBJETIVOS DE WAYAK — identidad de ESTE tenant, no código compartido.
 *
 * WAYAK vende telemetría, monitoreo y administración de flotilla, más Centinela
 * y sus alertas con IA: el paso «¿Qué quieres lograr?» sólo ofrece eso. La
 * familia de forks (CC-CONSOLE, NUVEX…) trae un catálogo genérico (vender,
 * conseguir clientes, controlar accesos, medir horas…): AL SINCRONIZAR CÓDIGO
 * COMPARTIDO NO SE PISA ESTE MAPA. Su espejo en el backend es `WAYAK_GOALS`
 * (functions/src/modules/onboarding/onboardingState.js): mismas claves, mismo
 * orden; el backend rechaza cualquier otra.
 *
 * Objetivo → app real del catálogo de este tenant. Un objetivo sólo se ofrece
 * si esa app está habilitada aquí.
 */
export const WAYAK_GOAL_APPS = {
  // Ubicación en tiempo real, recorridos, velocidad y eventos → el mapa.
  telemetry: { category: "rastreo-telemetria", app: "lite", icon: "ri-map-pin-line" },
  // Consumo, cargas y descargas con sensor → Reportes (combustible).
  fuel: { category: "rastreo-telemetria", app: "reportes", icon: "ri-gas-station-line" },
  // Cámaras a bordo (dashcam) y evidencia de eventos → se ven desde el mapa.
  video: { category: "rastreo-telemetria", app: "lite", icon: "ri-vidicon-line" },
  // Rutas, paradas, geocercas y entregas → Torre de Control.
  trip_monitoring: { category: "rastreo-telemetria", app: "torre-control", icon: "ri-route-line" },
  // Unidades, conductores, dispositivos y documentos → Administración de Flotilla.
  fleet_admin: { category: "rastreo-telemetria", app: "admin", icon: "ri-truck-line" },
  // Centinela: detección automática de riesgos, avisos y llamadas de protocolo.
  centinela_ai: { category: "rastreo-telemetria", app: "monitoreo-ia", icon: "ri-shield-flash-line" },
  // Guías, ligas de rastreo y estatus para los clientes → Envíos.
  shipments: { category: "rastreo-telemetria", app: "envios-link", icon: "ri-box-3-line" },
  // Kilometraje, paradas, velocidad, temperatura… → Reportes.
  reports: { category: "rastreo-telemetria", app: "reportes", icon: "ri-bar-chart-box-line" },
} as const satisfies Record<string, GoalTarget>;

export type OnboardingGoal = keyof typeof WAYAK_GOAL_APPS;

/** Claves del catálogo, en el orden en que se muestran. */
export const WAYAK_GOALS = Object.keys(WAYAK_GOAL_APPS) as OnboardingGoal[];

const isKnownGoal = (goal: string): goal is OnboardingGoal => goal in WAYAK_GOAL_APPS;

/**
 * Objetivos a mostrar: los que el backend acepta Y cuya app existe en este
 * tenant. Lo que el backend mande y aquí no se conozca (o al revés) se
 * descarta. Si el cruce queda vacío (catálogo sin cargar) se ofrecen todos los
 * aceptados, para no dejar el paso en blanco.
 *
 * @param isAppAvailable `isAppEnabled` de `config/appsConfig`
 */
export function goalsForTenant(
  isAppAvailable: (category: string, app: string) => boolean,
  accepted: readonly string[] = WAYAK_GOALS,
): OnboardingGoal[] {
  const known = WAYAK_GOALS.filter((goal) => accepted.includes(goal));
  const matching = known.filter((goal) => {
    const target = WAYAK_GOAL_APPS[goal];
    return isAppAvailable(target.category, target.app);
  });
  return matching.length ? matching : known;
}

/** Sólo los objetivos que existen hoy: una clave vieja ya guardada se ignora. */
export function knownGoals(goals: readonly string[] | null | undefined): OnboardingGoal[] {
  return [...new Set(goals ?? [])].filter(isKnownGoal);
}

/** Marca o desmarca un objetivo sin repetirlo. */
export function toggleGoal<T extends string>(goals: readonly T[], goal: T): T[] {
  return goals.includes(goal) ? goals.filter((g) => g !== goal) : [...goals, goal];
}

// ---------------------------------------------------------------------------
// Zona horaria
// ---------------------------------------------------------------------------

/**
 * Zonas de México, de la más usada a la menos. Van PRIMERO en la lista: quien
 * opera una flotilla aquí no debe buscar entre cientos de ids que empiezan por
 * África. La etiqueta de cada una vive en el locale (`timezones.<id>`).
 */
export const MEXICO_TIMEZONES = [
  "America/Mexico_City",
  "America/Monterrey",
  "America/Merida",
  "America/Cancun",
  "America/Chihuahua",
  "America/Ciudad_Juarez",
  "America/Ojinaga",
  "America/Matamoros",
  "America/Hermosillo",
  "America/Mazatlan",
  "America/Bahia_Banderas",
  "America/Tijuana",
] as const;

/** Regiones IANA en el orden en que se listan (después de «México»). */
export const TIMEZONE_REGIONS = [
  "America",
  "Europe",
  "Asia",
  "Africa",
  "Australia",
  "Pacific",
  "Atlantic",
  "Indian",
  "Antarctica",
  "Arctic",
] as const;

/** Grupo de lo que no cae en ninguna región (UTC, `Etc/…`). */
export const OTHER_REGION = "Other";

export interface TimezoneGroup {
  /** `Mexico`, una de `TIMEZONE_REGIONS` u `Other`: clave de `timezoneRegions.*`. */
  region: string;
  zones: string[];
}

/** Zona horaria del navegador, o cadena vacía si no se puede leer. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

/** ¿El navegador conoce esta zona? */
export function isSupportedTimezone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Zonas que conoce el navegador. Los antiguos no traen `supportedValuesOf`:
 * ahí se ofrecen las de México que sí acepten, que es lo que más se elige.
 */
export function supportedTimezones(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    const list = intl.supportedValuesOf ? intl.supportedValuesOf("timeZone") : [];
    if (list.length) return list;
  } catch {
    /* se cae a la lista corta */
  }
  return MEXICO_TIMEZONES.filter(isSupportedTimezone);
}

/** ¿Es una zona con ciudad (`America/Mexico_City`) y no `UTC`, `GMT` o `Etc/GMT+6`? */
export function isGeographicTimezone(zone: string | null | undefined): boolean {
  return !!zone && zone.includes("/") && !zone.startsWith("Etc/");
}

/**
 * Desfase de una zona respecto a UTC en un instante, en minutos (México
 * centro = -360). `null` si el navegador no conoce la zona.
 */
export function timezoneOffsetMinutes(zone: string, at: Date = new Date()): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(at);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
    if (!Number.isFinite(asUtc)) return null;
    return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
  } catch {
    return null;
  }
}

/** `UTC−6`, `UTC+5:30`, `UTC`. */
export function formatUtcOffset(minutes: number | null): string {
  if (minutes === null) return "";
  if (minutes === 0) return "UTC";
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  return `UTC${minutes < 0 ? "−" : "+"}${hours}${rest ? `:${String(rest).padStart(2, "0")}` : ""}`;
}

/** `America/Argentina/Buenos_Aires` → `Argentina / Buenos Aires`; `UTC` → `UTC`. */
export function timezoneCityLabel(zone: string): string {
  const slash = zone.indexOf("/");
  const rest = slash < 0 ? zone : zone.slice(slash + 1);
  return rest.replace(/_/g, " ").replace(/\//g, " / ");
}

/**
 * La lista del desplegable: «México» primero y el resto por región.
 * `current` (la zona ya guardada o la del navegador) siempre aparece, aunque
 * el navegador no la liste (un alias viejo o un `Etc/GMT+6` guardado antes).
 */
export function groupTimezones(supported: readonly string[], current = ""): TimezoneGroup[] {
  const all = [...new Set([...supported, ...(current ? [current] : [])])];
  const present = new Set(all);
  const mexico = MEXICO_TIMEZONES.filter((zone) => present.has(zone));
  const taken = new Set<string>(mexico);
  const groups: TimezoneGroup[] = mexico.length ? [{ region: "Mexico", zones: mexico }] : [];

  for (const region of TIMEZONE_REGIONS) {
    const zones = all.filter((zone) => !taken.has(zone) && zone.startsWith(`${region}/`)).sort();
    zones.forEach((zone) => taken.add(zone));
    if (zones.length) groups.push({ region, zones });
  }
  const other = all.filter((zone) => !taken.has(zone)).sort();
  if (other.length) groups.push({ region: OTHER_REGION, zones: other });
  return groups;
}

export type TimezoneSource = "saved" | "browser" | "matched" | "none";

export interface InitialTimezone {
  timezone: string;
  /**
   * De dónde salió, para que el texto de ayuda diga la verdad:
   * `saved` ya estaba guardada · `browser` es la del navegador · `matched` el
   * navegador no dio una ciudad y se eligió la zona de México con su misma
   * hora · `none` no se pudo deducir nada.
   */
  source: TimezoneSource;
}

/**
 * Zona con la que se precarga el paso «Tu empresa».
 *
 * Si el navegador reporta una zona con ciudad, es esa. Si reporta una zona NO
 * geográfica (`Etc/GMT+6`, `UTC`…: pasa con equipos configurados por desfase y
 * no por ciudad), se busca la zona de México que hoy tiene ese mismo desfase
 * (`Etc/GMT+6` → `America/Mexico_City`) en vez de guardar el id crudo.
 *
 * @param offsetOf desfase actual de una zona en minutos (inyectable en pruebas)
 */
export function resolveInitialTimezone(
  saved: string | null | undefined,
  browser: string | null | undefined,
  supported: readonly string[],
  offsetOf: (zone: string) => number | null = (zone) => timezoneOffsetMinutes(zone),
): InitialTimezone {
  if (saved) return { timezone: saved, source: "saved" };
  const zone = browser || "";
  if (isGeographicTimezone(zone)) return { timezone: zone, source: "browser" };

  const target = zone ? offsetOf(zone) : null;
  if (target !== null) {
    const known = new Set(supported);
    const match = MEXICO_TIMEZONES.find((candidate) => known.has(candidate) && offsetOf(candidate) === target);
    if (match) return { timezone: match, source: "matched" };
  }
  // Sin equivalente en México: se deja la del navegador sólo si es elegible
  // (`UTC` lo es siempre, aunque algunos navegadores no lo listen).
  if (zone && (zone === "UTC" || supported.includes(zone))) return { timezone: zone, source: "browser" };
  return { timezone: "", source: "none" };
}

// ---------------------------------------------------------------------------
// Invitaciones al equipo
// ---------------------------------------------------------------------------

/** Correos por envío (mismo tope que el backend, `MAX_PER_REQUEST`). */
export const MAX_INVITES_PER_SEND = 10;

/** Misma forma que exige el transporte de correo del backend (`pilotMailService`). */
const EMAIL_RE = /^[a-z0-9._%+-]{1,64}@[a-z0-9](?:[a-z0-9.-]{0,250}[a-z0-9])?\.[a-z]{2,24}$/i;

export function isValidInviteEmail(value: string): boolean {
  return value.length <= 254 && EMAIL_RE.test(value) && !value.includes("..");
}

/** Correos de un texto libre: separados por coma, punto y coma, espacio o salto de línea. */
export function parseInviteEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
}

export type InviteRejection = "invalid" | "duplicate" | "self" | "limit";

export interface InviteDraft {
  /** Lista resultante (los que ya estaban + los nuevos válidos). */
  emails: string[];
  /** Lo que no entró y por qué, en el orden en que se escribió. */
  rejected: Array<{ email: string; reason: InviteRejection }>;
}

/**
 * Suma a la lista los correos de `text`: sólo con forma de correo, sin
 * repetidos, nunca el de quien invita y hasta `max` por envío.
 */
export function addInviteEmails(
  current: readonly string[],
  text: string,
  { selfEmail = "", max = MAX_INVITES_PER_SEND }: { selfEmail?: string | null; max?: number } = {},
): InviteDraft {
  const emails = [...current];
  const rejected: InviteDraft["rejected"] = [];
  const own = (selfEmail || "").trim().toLowerCase();
  for (const email of parseInviteEmails(text)) {
    if (!isValidInviteEmail(email)) rejected.push({ email, reason: "invalid" });
    else if (own && email === own) rejected.push({ email, reason: "self" });
    else if (emails.includes(email)) rejected.push({ email, reason: "duplicate" });
    else if (emails.length >= max) rejected.push({ email, reason: "limit" });
    else emails.push(email);
  }
  return { emails, rejected };
}

/** Estado por correo que devuelve `POST /onboarding/invitations` o la lista guardada. */
export type InviteStatus =
  | "sent"
  | "already_member"
  | "failed"
  | "invalid"
  | "self"
  | "limit"
  | "pending"
  | "accepted"
  | "expired"
  | "revoked";

export interface InviteRow {
  email: string;
  status: InviteStatus;
}

/**
 * Lista que se pinta bajo el campo: lo ya guardado en el servidor y, encima,
 * el resultado del último envío (que manda cuando el correo se repite).
 */
export function mergeInviteRows(saved: readonly InviteRow[], latest: readonly InviteRow[]): InviteRow[] {
  const byEmail = new Map<string, InviteRow>();
  for (const row of saved) byEmail.set(row.email, row);
  for (const row of latest) byEmail.set(row.email, row);
  const order = [...latest.map((r) => r.email), ...saved.map((r) => r.email)];
  return [...new Set(order)].map((email) => byEmail.get(email) as InviteRow);
}
